/**
 * API Error structure from axios/fetch responses.
 *
 * `data` is loosely typed because different backends return different shapes
 * (tattle.net uses `message` / `detail`, or an `errors.business` list for a
 * rejection the admin can act on; tattle-inbox uses `error` plus an optional
 * `unauthorized` list and an `allowedDomains` hint). The `getErrorMessage`
 * helper below walks those variants in priority order.
 */
export interface ApiError {
  response?: {
    status: number
    data?: {
      message?: string
      detail?: string
      title?: string
      /**
       * RFC 9110 problem-details validation shape returned by tattle.net's FluentValidation
       * pipeline: { errors: { validation: [{ field, code, message }] } }. The per-rule `message`
       * is the only text that tells a user what they actually did wrong -- without this branch
       * the UI falls through to axios's generic "Request failed with status code 400".
       */
      errors?: {
        validation?: { field?: string; code?: string; message?: string }[]
        /**
         * A domain rule refused the request — distinct from `validation`, which means the
         * request never got that far. Carries text an admin can act on, e.g. the partner
         * saying the stored credentials were rejected. `code` is a stable machine-readable
         * identifier for the specific rule that fired (see `isOnlineReviewsNotEnabledError`,
         * which keys on one); `message` is the human-readable text.
         */
        business?: { code?: string; message?: string }[]
        [field: string]: unknown // ASP.NET Core's ValidationProblemDetails: field name -> string[]
      }
      error?: string
      unauthorized?: string[]
      allowedDomains?: string[]
    }
  }
  message?: string
}

/**
 * Type guard to check if error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null && 'response' in error
}

/**
 * Gets the HTTP status code from an API error, or undefined if not available
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (!isApiError(error)) {
    return undefined
  }
  return error.response?.status
}

/**
 * The business-rule code tattle.net's ProblemDetails body carries for this specific gate --
 * the one stable, documented contract for it (see `isOnlineReviewsNotEnabledError`). Exported
 * so a caller can recognize the same rejection without re-deriving the literal.
 */
export const ONLINE_REVIEWS_CHATMETER_INACTIVE_ERROR_CODE = 'onlineReviews.access.chatmeterInactive'

/**
 * True when `error` is the 403 tattle.net throws for a merchant whose Chatmeter integration
 * is inactive or unmapped, as opposed to a genuine permission-denied 403. Keyed on status +
 * the `errors.business[].code` tattle.net's ProblemDetails body carries for this gate --
 * not the request path or the message text, neither of which is a stable contract -- so a
 * caller that recognizes it should skip the generic 403 handling (interceptor toast, "Failed
 * to load ..." messaging) and show its own informational state instead.
 */
export function isOnlineReviewsNotEnabledError(error: unknown): boolean {
  return (
    isApiError(error) &&
    error.response?.status === 403 &&
    !!error.response?.data?.errors?.business?.some(
      entry => entry?.code === ONLINE_REVIEWS_CHATMETER_INACTIVE_ERROR_CODE
    )
  )
}

/**
 * Determines if an error should set the store error state.
 * Returns false for 403 errors since they're handled globally by the HTTP interceptor.
 */
export function shouldSetStoreError(error: unknown): boolean {
  if (!isApiError(error)) {
    return true
  }
  return error.response?.status !== 403
}

/**
 * Gets an appropriate error message from an API error response or standard Error.
 * Returns empty string for 403 errors, on the assumption the HTTP interceptor toasts them.
 * NOT true on a `skipAuthHandling` client (`tattle-http.ts:102`) — there the caller must
 * surface it, or a 403 is silent. See `.claude/rules/error-handling.md` rule 5.
 *
 * Priority (API errors):
 *   1. Structured `{ error, unauthorized, allowedDomains }` — composed into a
 *      single human-readable sentence. This matches the tattle-inbox sink's
 *      validation response shape (e.g. the 422 from `POST /messages` when a
 *      recipient isn't on an allowed internal domain).
 *   2. `errors.validation[].message` — tattle.net's RFC 9110 problem-details shape, where the
 *      per-rule message is the only text that says what the user did wrong.
 *   3. `errors.business[].message` — a domain rule refused an otherwise well-formed request,
 *      e.g. a partner rejecting the credentials stored for a merchant.
 *   4. `errors.<FieldName>[]` — ASP.NET Core's native ValidationProblemDetails shape (model
 *      binding / data-annotation failures), keyed by field name instead of a `validation` array.
 *   5. `message` / `detail` / `title` — the tattle.net / v3-api convention.
 *   6. `error` alone — less structured endpoints.
 *   7. Axios `error.message` — transport-level failures (network, timeout).
 *   8. Caller's `fallbackMessage`.
 */
export function getErrorMessage(error: unknown, fallbackMessage: string): string {
  // Handle API errors (axios/fetch with response)
  if (isApiError(error)) {
    // Don't set error message for 403 - the interceptor handles it
    if (error.response?.status === 403) {
      return ''
    }
    const data = error.response?.data
    if (data) {
      // Structured validation response — compose a full sentence
      // ("<error>: <unauthorized joined> (allowed: <allowedDomains joined>)").
      // The sink returns this shape; other endpoints that adopt the same
      // convention get the same rendering for free.
      if (data.error) {
        let msg = data.error
        if (data.unauthorized && data.unauthorized.length > 0) {
          msg += `: ${data.unauthorized.join(', ')}`
        }
        if (data.allowedDomains && data.allowedDomains.length > 0) {
          msg += ` (allowed: ${data.allowedDomains.join(', ')})`
        }
        return msg
      }
      // Validation rules first: they name the specific field and rule that failed, which is
      // strictly more useful than the envelope's generic title.
      const validation = data.errors?.validation
      if (validation?.length) {
        const messages = validation.map((v) => v.message).filter((m): m is string => !!m)
        if (messages.length) return messages.join(' ')
      }
      // A business rejection the admin can act on — "Qu rejected the credentials
      // stored for this merchant" says what to fix where a fixed "Failed to X"
      // does not. Several entries are joined so none is silently dropped.
      // It sits below validation because the two are raised at different stages: a
      // request malformed enough to fail validation never reaches the domain rule
      // that would raise this, so in practice only one of them is ever present.
      const business = data.errors?.business
        ?.map(entry => entry?.message)
        .filter((message): message is string => !!message)
      if (business && business.length > 0) return business.join(' ')
      // ASP.NET Core's native ValidationProblemDetails shape: errors keyed directly by field
      // name (no `validation` wrapper), e.g. { CycleId: ["The value 'NaN' is not valid..."] }.
      // Wins over `title`, which is just the generic "One or more validation errors occurred."
      // A blank/whitespace-only message is filtered out too -- otherwise it would still count as
      // "found" and suppress the title/message fallback below with an empty string.
      if (data.errors) {
        const fieldMessages = Object.entries(data.errors)
          .filter(([key]) => key !== 'validation')
          .flatMap(([, v]) => (Array.isArray(v) ? v.filter((m): m is string => typeof m === 'string' && m.trim() !== '') : []))
        if (fieldMessages.length) return fieldMessages.join(' ')
      }
      if (data.message) return data.message
      if (data.detail) return data.detail
      if (data.title) return data.title
    }
    return error.message || fallbackMessage
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message || fallbackMessage
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error
  }

  return fallbackMessage
}
